/**
 * Alternative scene archetypes \u2014 vizz.fm-inspired scenes that complement
 * the BioclastScene's particle-galaxy style. Each scene implements the same
 * minimal interface so the Renderer can swap between them without knowing
 * their internals.
 *
 * Scenes:
 *   - MeshGridScene       (vizz.fm "Mesh Grid")        \u2014 wireframe terrain with perlin + audio displacement
 *   - WavyFormScene       (vizz.fm "Wavy Form")        \u2014 stacked rotating waveform polylines
 *   - DotGridScene        (vizz.fm "Dot Grid")         \u2014 instanced dot wall, audio-displaced
 *   - FrequencyBarsScene  (vizz.fm "Frequency Hallway") \u2014 mirrored stacked spectrum bars
 *
 * All scenes are self-contained Three.js Scene + Camera + update(dt, features).
 * Compact intentionally; not aiming to be feature-complete duplicates of
 * vizz.fm \u2014 just visually credible scene templates that work with our
 * existing post-FX stack, background system, and recording pipeline.
 */

import * as THREE from "three";
import { AudioFeatures } from "../audio/types";
import { LiveMathParticleScene } from "./liveMathParticleScene";
import { BioclastParams } from "./scenes/bioclast";

export interface AltSceneApi {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  setSize(w: number, h: number): void;
  /** Called every frame after `setFeatures`. */
  update(dt: number, params: BioclastParams, features: AudioFeatures): void;
  /**
   * Optional hook called by the main renderer BEFORE the composer's
   * RenderPass runs. GPGPU scenes use this to advance their simulation
   * step into private render targets — doing so during the composer's
   * traversal would stomp the writeBuffer FBO binding and the particles
   * would render to the canvas backbuffer instead of the composer input.
   */
  prerender?(renderer: THREE.WebGLRenderer, dt: number, time: number): void;
  /** Read-only backdrop tint hint (the global Renderer mirrors to DOM). */
  readonly backdrop: { r: number; g: number; b: number; alpha: number; blurPx: number };
  /** Latest beat envelope for HUD. */
  readonly beatLevel: number;
  /** How the global rig should treat this scene's camera. */
  readonly cameraPersonality: CameraPersonality;
  dispose(): void;
}

/**
 * Per-scene hint to the renderer's alt-scene camera rig:
 *  - `orbit`           : full orbital + parallax around target (default)
 *  - `staticParallax`  : legacy gentle drift mode for authored one-off shots
 *  - `hallwayFly`      : legacy forward dolly for corridor shots
 *  - `closeOrbit`      : legacy tighter orbit for cinematic close-ups
 */
export type CameraPersonality = "orbit" | "staticParallax" | "hallwayFly" | "closeOrbit";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------
function hsl(h: number, s: number, l: number): THREE.Color {
  const c = new THREE.Color();
  c.setHSL(((h % 360) + 360) % 360 / 360, s, l);
  return c;
}

function smoothBeat(prev: number, beat: number, dt: number): number {
  // Quick attack, slow decay.
  if (beat > prev) return beat;
  return prev * Math.exp(-dt * 5);
}

// 2D value-noise lookup. Cheap, deterministic, smooth-enough for displacement.
function vnoise(x: number, y: number): number {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const r = (a: number, b: number): number => {
    // Hash-noise in 0..1.
    const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
    return s - Math.floor(s);
  };
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = r(xi, yi), b = r(xi + 1, yi), c = r(xi, yi + 1), d = r(xi + 1, yi + 1);
  return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v;
}

// ---------------------------------------------------------------------------
// MeshGridScene \u2014 wireframe terrain
// Supports vizz.fm-style variants: flat / dome+radial-waves / 3-stacked /
// kaleidoscope-bowl / noisy-mountain. Driven by `meshGrid*` params on
// BioclastParams (radialMix, bend, circleShape, noiseAmount, yStacks).
// ---------------------------------------------------------------------------
export class MeshGridScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 6, g: 6, b: 18, alpha: 0.35, blurPx: 30 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private gridW = 100;
  private gridH = 100;
  private cellSize = 1.4;
  private time = 0;

  // One LineSegments per Y-stack so we can hue-shift each independently and
  // also support `meshGridYStacks > 1` (vizz "gridTile rows" stacked look).
  private stacks: {
    mesh: THREE.LineSegments;
    geo: THREE.BufferGeometry;
    mat: THREE.LineBasicMaterial;
    positions: Float32Array;
    basePositions: Float32Array;
  }[] = [];
  private currentYStacks = 0;

  constructor(width: number, height: number) {
    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 2000);
    this.camera.position.set(0, 28, 60);
    this.camera.lookAt(0, 0, 0);
    this.rebuildStacks(1);
  }

  private rebuildStacks(count: number): void {
    // Tear down existing.
    for (const s of this.stacks) {
      this.scene.remove(s.mesh);
      s.geo.dispose();
      s.mat.dispose();
    }
    this.stacks = [];

    for (let s = 0; s < count; s++) {
      const verts: number[] = [];
      const segs: number[] = [];
      const half = (n: number, sz: number): number[] => {
        const arr: number[] = [];
        for (let i = 0; i < n; i++) arr.push((i - (n - 1) / 2) * sz);
        return arr;
      };
      const xs = half(this.gridW, this.cellSize);
      const zs = half(this.gridH, this.cellSize);
      const idx = (i: number, j: number): number => i * this.gridH + j;
      for (let i = 0; i < this.gridW; i++) {
        for (let j = 0; j < this.gridH; j++) verts.push(xs[i], 0, zs[j]);
      }
      for (let i = 0; i < this.gridW; i++) {
        for (let j = 0; j < this.gridH; j++) {
          if (i + 1 < this.gridW) segs.push(idx(i, j), idx(i + 1, j));
          if (j + 1 < this.gridH) segs.push(idx(i, j), idx(i, j + 1));
        }
      }
      const positions = new Float32Array(verts);
      const basePositions = new Float32Array(verts);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geo.setIndex(segs);
      const mat = new THREE.LineBasicMaterial({
        color: 0xa02dc5,
        transparent: true,
        opacity: 0.9,
      });
      const mesh = new THREE.LineSegments(geo, mat);
      this.scene.add(mesh);
      this.stacks.push({ mesh, geo, mat, positions, basePositions });
    }
    this.currentYStacks = count;
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, p: BioclastParams, f: AudioFeatures): void {
    this.time += dt * (0.4 + p.flowSpeed * 0.6);
    this.beatLevel = smoothBeat(this.beatLevel, f.beat, dt);

    const desiredStacks = Math.max(1, Math.min(5, Math.floor(p.meshGridYStacks)));
    if (desiredStacks !== this.currentYStacks) this.rebuildStacks(desiredStacks);

    const noiseAmt = p.meshGridNoiseAmount * (1 + f.bass * 1.5 * p.bassGravity);
    const audioAmp = 0.4 + f.level * 3.5 + this.beatLevel * 1.5;
    const radialMix = Math.max(0, Math.min(1, p.meshGridRadialMix));
    const bend = p.meshGridBend;
    const W = this.gridW, H = this.gridH;
    // Half-extent for circle-mask + bend curvature.
    const halfX = (W * this.cellSize) / 2;
    const maxR = halfX;
    const stackSpacing = p.meshGridYStackSpacing;

    for (let sIdx = 0; sIdx < this.stacks.length; sIdx++) {
      const s = this.stacks[sIdx];
      const yOffset = (sIdx - (this.stacks.length - 1) / 2) * stackSpacing;
      // Per-stack mirror: alternate stacks flip vertically (vizz mirror look).
      const flip = this.stacks.length > 1 && sIdx % 2 === 1 ? -1 : 1;

      for (let i = 0; i < W; i++) {
        for (let j = 0; j < H; j++) {
          const k = (i * H + j) * 3;
          const x = s.basePositions[k];
          const z = s.basePositions[k + 2];
          const r = Math.sqrt(x * x + z * z);
          // Optional circle mask: collapse out-of-circle verts to NaN-ish low
          // values that LineBasicMaterial won't render at full opacity. Cheap
          // trick: scale down to a tiny invisible point.
          if (p.meshGridCircleShape && r > maxR) {
            s.positions[k] = 0;
            s.positions[k + 1] = 0;
            s.positions[k + 2] = 0;
            continue;
          }
          // Radial wave (origin-out) blended with linear sine in flat mode.
          const radialWave = Math.sin(r * 0.18 - this.time * 2.4) * audioAmp;
          const linearWave = Math.sin((x + z) * 0.12 - this.time * 1.8) * audioAmp * 0.5;
          const wave = radialWave * radialMix + linearWave * (1 - radialMix);
          // Bend: dome-style elevation that scales with radius. Negative bend
          // pushes the centre UP (mountain), positive pushes DOWN (bowl).
          const bendY = -bend * (1 - r / maxR) * 6;
          // Perlin-ish noise.
          const n = (vnoise(x * 0.05 + this.time * 0.3, z * 0.05 + sIdx * 7) - 0.5) * noiseAmt;

          s.positions[k] = x;
          s.positions[k + 1] = (wave + bendY + n) * flip + yOffset;
          s.positions[k + 2] = z;
        }
      }
      (s.geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;

      const hue = p.paletteHue + sIdx * p.meshGridYStackHueShift;
      if (p.meshGridLineColor && p.meshGridLineColor.length > 0) {
        s.mat.color.set(p.meshGridLineColor);
        // When using an explicit grid color, modulate brightness with beat so
        // black/white grids still pulse (matches vizz's emissive-on-audio look).
        const brightnessBoost = 1 + this.beatLevel * 0.6 + f.bass * 0.3;
        s.mat.color.multiplyScalar(brightnessBoost);
      } else {
        const lightness = THREE.MathUtils.clamp(0.24 + p.intensity * 0.12 + this.beatLevel * 0.12, 0.22, 0.54);
        s.mat.color = hsl(hue, p.saturation, lightness);
      }
      s.mat.opacity = THREE.MathUtils.clamp(0.18 + p.opacity * 0.42 + f.level * 0.12, 0.18, 0.72);
      s.mesh.rotation.y = (this.scene.rotation.y) + dt * 0.05 * p.cameraSpin;
    }
    this.scene.rotation.y += dt * 0.05 * p.cameraSpin;

    const c = hsl(p.paletteHue, 0.5, 0.18);
    this.backdrop.r = Math.round(c.r * 80 + this.beatLevel * 40);
    this.backdrop.g = Math.round(c.g * 60 + f.mid * 40);
    this.backdrop.b = Math.round(c.b * 100 + f.treble * 30);
    this.backdrop.alpha = 0.35 + f.bass * 0.4;
  }

  dispose(): void {
    for (const s of this.stacks) {
      s.geo.dispose();
      s.mat.dispose();
    }
    this.stacks = [];
  }
}

// ---------------------------------------------------------------------------
// WavyFormScene \u2014 200 stacked sin-wave polylines, audio-driven amplitude
// ---------------------------------------------------------------------------
export class WavyFormScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 12, g: 6, b: 12, alpha: 0.4, blurPx: 30 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private lines: THREE.Line[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private mats: THREE.LineBasicMaterial[] = [];
  private positions: Float32Array[] = [];
  private time = 0;
  private lineCount = 80;
  private samplesPerLine = 200;

  constructor(width: number, height: number) {
    this.camera = new THREE.PerspectiveCamera(70, width / height, 0.1, 100);
    this.camera.position.set(0, 0, 6);
    this.camera.lookAt(0, 0, 0);

    for (let i = 0; i < this.lineCount; i++) {
      const N = this.samplesPerLine;
      const positions = new Float32Array(N * 3);
      for (let s = 0; s < N; s++) {
        const t = s / (N - 1);
        positions[s * 3] = (t - 0.5) * 8;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.LineBasicMaterial({
        color: hsl(200 + i * 1.5, 0.85, 0.55),
        transparent: true,
        opacity: 0.3,
      });
      const line = new THREE.Line(geo, mat);
      this.scene.add(line);
      this.lines.push(line);
      this.geos.push(geo);
      this.mats.push(mat);
      this.positions.push(positions);
    }
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, p: BioclastParams, f: AudioFeatures): void {
    this.time += dt * (0.5 + p.flowSpeed * 0.8);
    this.beatLevel = smoothBeat(this.beatLevel, f.beat, dt);
    const N = this.samplesPerLine;
    const amp = 0.5 + f.bass * 1.5 * p.bassGravity + this.beatLevel * 0.6;
    const trebleSpike = f.treble * 1.4 * p.trebleShimmer;
    for (let i = 0; i < this.lineCount; i++) {
      const pos = this.positions[i];
      const phase = i * 0.12 + this.time * (0.6 + i * 0.01);
      const yOffset = (i - this.lineCount / 2) * 0.05;
      for (let s = 0; s < N; s++) {
        const t = s / (N - 1);
        const x = (t - 0.5) * 8;
        const wave = Math.sin(x * 1.4 + phase) * amp +
                     Math.sin(x * 4 + phase * 1.7) * trebleSpike * 0.4;
        pos[s * 3 + 1] = yOffset + wave * (1 - Math.abs(t - 0.5) * 1.4);
      }
      (this.geos[i].attributes.position as THREE.BufferAttribute).needsUpdate = true;
      this.mats[i].color = hsl(
        p.paletteHue + i * 1.6 + this.time * 8,
        p.saturation,
        THREE.MathUtils.clamp(0.46 + p.intensity * 0.08 + this.beatLevel * 0.24, 0.44, 0.78),
      );
      this.mats[i].opacity = THREE.MathUtils.clamp(
        (0.32 + Math.min(0.62, f.level * 0.6) + p.intensity * 0.04) * Math.max(0.35, p.opacity),
        0.18,
        0.86,
      );
    }
    this.scene.rotation.z = Math.sin(this.time * 0.18) * 0.06 * p.cameraSpin;
    const c = hsl(p.paletteHue, 0.5, 0.15);
    this.backdrop.r = Math.round(c.r * 60);
    this.backdrop.g = Math.round(c.g * 40);
    this.backdrop.b = Math.round(c.b * 80);
    this.backdrop.alpha = 0.3 + f.level * 0.4;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
  }
}

// ---------------------------------------------------------------------------
// DotGridScene \u2014 InstancedMesh dot wall (3D), audio-driven scale
// ---------------------------------------------------------------------------
export class DotGridScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 4, g: 4, b: 8, alpha: 0.3, blurPx: 20 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private mesh: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();
  private color = new THREE.Color();
  private time = 0;
  private W = 64;
  private H = 36;

  constructor(width: number, height: number) {
    this.camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 200);
    this.camera.position.set(0, 0, 50);
    this.camera.lookAt(0, 0, 0);

    const total = this.W * this.H;
    const geo = new THREE.SphereGeometry(0.18, 8, 8);
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.68 });
    this.mesh = new THREE.InstancedMesh(geo, mat, total);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(total * 3), 3);
    this.scene.add(this.mesh);
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, p: BioclastParams, f: AudioFeatures): void {
    this.time += dt;
    this.beatLevel = smoothBeat(this.beatLevel, f.beat, dt);
    const W = this.W, H = this.H;
    const sp = 0.7;
    let idx = 0;
    for (let i = 0; i < W; i++) {
      for (let j = 0; j < H; j++) {
        const x = (i - (W - 1) / 2) * sp;
        const y = (j - (H - 1) / 2) * sp;
        // Radial distance for wave ripples.
        const r = Math.sqrt(x * x + y * y);
        const wave = Math.sin(r * 0.4 - this.time * 3) * 0.5 + 0.5;
        const audio = (f.bass * (1 - r / 30) + f.mid * 0.5 + f.treble * (r / 30)) * 1.5;
        const scale = 0.4 + wave * 0.6 + audio * (1 + this.beatLevel) +
                      this.beatLevel * 0.6 * Math.exp(-r * 0.05);
        this.dummy.position.set(x, y, 0);
        this.dummy.scale.setScalar(Math.max(0.05, Math.min(4, scale)));
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(idx, this.dummy.matrix);
        // Color: hue gradient by Y, brightness by audio.
        this.color.copy(hsl(p.paletteHue + j * 4, p.saturation, 0.3 + Math.min(0.5, audio * 0.5 + this.beatLevel * 0.3)));
        this.mesh.setColorAt(idx, this.color);
        idx++;
      }
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.scene.rotation.y = Math.sin(this.time * 0.2) * 0.12 * p.cameraSpin;
    const c = hsl(p.paletteHue, 0.4, 0.12);
    this.backdrop.r = Math.round(c.r * 40);
    this.backdrop.g = Math.round(c.g * 40);
    this.backdrop.b = Math.round(c.b * 60);
    this.backdrop.alpha = 0.25 + f.level * 0.3;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

// ---------------------------------------------------------------------------
// FrequencyBarsScene \u2014 3D mirrored spectrum bars (vizz.fm Frequency Hallway)
// ---------------------------------------------------------------------------
export class FrequencyBarsScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 8, g: 4, b: 12, alpha: 0.35, blurPx: 25 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private mesh: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();
  private color = new THREE.Color();
  private time = 0;
  private barCount = 96;
  private yCopies = 4;
  private zCopies = 3;

  constructor(width: number, height: number) {
    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 500);
    this.camera.position.set(0, 8, 38);
    this.camera.lookAt(0, 0, 0);

    const total = this.barCount * this.yCopies * this.zCopies;
    const geo = new THREE.BoxGeometry(0.3, 1, 0.3);
    geo.translate(0, 0.5, 0); // anchor at the base
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.mesh = new THREE.InstancedMesh(geo, mat, total);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(total * 3), 3);
    this.scene.add(this.mesh);
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, p: BioclastParams, f: AudioFeatures): void {
    this.time += dt;
    this.beatLevel = smoothBeat(this.beatLevel, f.beat, dt);
    let idx = 0;
    const spacingX = 0.45;
    const spacingY = 5;
    const spacingZ = 5;
    for (let yi = 0; yi < this.yCopies; yi++) {
      const yOffset = (yi - (this.yCopies - 1) / 2) * spacingY;
      for (let zi = 0; zi < this.zCopies; zi++) {
        const zOffset = (zi - (this.zCopies - 1) / 2) * spacingZ;
        for (let i = 0; i < this.barCount; i++) {
          // Bar value: synthesise a fake spectrum from 3 bands + noise.
          const t = i / (this.barCount - 1);
          let v = 0;
          if (t < 0.25) v = f.bass * (1 - t * 2);
          else if (t < 0.6) v = f.mid * Math.sin((t - 0.25) * 9);
          else v = f.treble * (1 - (t - 0.6) * 1.5);
          v = Math.max(0, v);
          const noise = vnoise(i * 0.3, this.time * 2 + zi) * 0.4;
          const height = 0.3 + (v + noise) * 6 + this.beatLevel * 1.5;
          const x = (i - (this.barCount - 1) / 2) * spacingX;
          const flip = yi % 2 === 0 ? 1 : -1; // mirror alternating Y stacks
          this.dummy.position.set(x, yOffset, zOffset);
          this.dummy.scale.set(1, height * flip, 1);
          this.dummy.updateMatrix();
          this.mesh.setMatrixAt(idx, this.dummy.matrix);
          this.color.copy(hsl(
            p.paletteHue + i * 1.5 + yi * 30,
            p.saturation,
            THREE.MathUtils.clamp(0.24 + p.intensity * 0.11 + this.beatLevel * 0.1, 0.24, 0.54),
          ));
          this.mesh.setColorAt(idx, this.color);
          idx++;
        }
      }
    }
    (this.mesh.material as THREE.MeshBasicMaterial).opacity = THREE.MathUtils.clamp(0.22 + p.opacity * 0.44, 0.2, 0.78);
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.scene.rotation.y = Math.sin(this.time * 0.15) * 0.1 * p.cameraSpin;
    const c = hsl(p.paletteHue, 0.5, 0.15);
    this.backdrop.r = Math.round(c.r * 60);
    this.backdrop.g = Math.round(c.g * 40);
    this.backdrop.b = Math.round(c.b * 80);
    this.backdrop.alpha = 0.3 + f.level * 0.35;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

// ---------------------------------------------------------------------------
// Factory + scene type registry
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// TexturedSphereScene — vizz.fm `audioReactiveSphere` parity.
// A single wireframe icosphere with per-vertex displacement driven by 3D
// noise + audio. The displacement direction is the vertex normal, so peaks
// stick out radially — reads as a sea-urchin / pollen-grain shape.
//
// Per-vertex hue is derived from the original spherical (lat,lon) coords,
// giving the rainbow look in vizz.fm's screenshot. Solid interior is a
// second darker mesh inside (vizz `showSolidInterior`).
// ---------------------------------------------------------------------------
export class TexturedSphereScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 4, g: 4, b: 12, alpha: 0.3, blurPx: 25 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private wire!: THREE.LineSegments;
  private wireGeo!: THREE.BufferGeometry;
  private wireMat!: THREE.LineBasicMaterial;
  private innerMesh!: THREE.Mesh;
  private innerGeo!: THREE.SphereGeometry;
  private innerMat!: THREE.MeshBasicMaterial;

  private basePositions!: Float32Array;
  private displacedPositions!: Float32Array;
  private smoothedRadii!: Float32Array;
  private vertexHues!: Float32Array;
  private vertexLons!: Float32Array;
  private colors!: Float32Array;
  private wireIndices!: Uint32Array;
  private vertexCount = 0;
  private currentDetail = 0;

  private rotation = 0;
  private time = 0;
  private tempColor = new THREE.Color();
  private accentColor = new THREE.Color();
  private baseColorRGB = new THREE.Color();

  constructor(width: number, height: number) {
    this.camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 200);
    this.camera.position.set(0, 0, 8);
    this.camera.lookAt(0, 0, 0);
    this.rebuildGeometry(96);
    this.innerGeo = new THREE.SphereGeometry(1, 32, 32);
    this.innerMat = new THREE.MeshBasicMaterial({ color: 0x000000 });
    this.innerMesh = new THREE.Mesh(this.innerGeo, this.innerMat);
    this.scene.add(this.innerMesh);
  }

  /**
   * Rebuild the wireframe sphere with the requested detail. Throws away
   * existing GPU buffers and per-vertex caches. Cost is O(detail^2);
   * detail=350 is ~123k verts which is heavy but only happens on preset
   * change, not per frame.
   */
  private rebuildGeometry(detail: number): void {
    if (this.currentDetail === detail) return;
    const clamped = Math.max(8, Math.min(256, Math.round(detail)));
    if (this.wire) {
      this.scene.remove(this.wire);
      this.wireGeo.dispose();
      this.wireMat.dispose();
    }
    const baseGeo = new THREE.SphereGeometry(1, clamped, clamped);
    const posAttr = baseGeo.getAttribute("position") as THREE.BufferAttribute;
    const arr = posAttr.array as Float32Array;
    this.vertexCount = arr.length / 3;
    this.basePositions = new Float32Array(arr);
    this.displacedPositions = new Float32Array(arr);
    this.smoothedRadii = new Float32Array(this.vertexCount);
    this.vertexHues = new Float32Array(this.vertexCount);
    this.vertexLons = new Float32Array(this.vertexCount);
    this.colors = new Float32Array(this.vertexCount * 3);
    for (let v = 0; v < this.vertexCount; v++) {
      const x = this.basePositions[v * 3];
      const y = this.basePositions[v * 3 + 1];
      const z = this.basePositions[v * 3 + 2];
      const lat = Math.asin(Math.max(-1, Math.min(1, y)));
      const lon = Math.atan2(x, z);
      this.vertexHues[v] = ((lat / Math.PI + 0.5) + lon / (Math.PI * 4) + 1) % 1;
      this.vertexLons[v] = lon;
    }
    const tri = baseGeo.getIndex();
    if (!tri) throw new Error("SphereGeometry missing index");
    const triArr = tri.array as Uint16Array | Uint32Array;
    const lines: number[] = [];
    for (let i = 0; i < triArr.length; i += 3) {
      const a = triArr[i], b = triArr[i + 1], c = triArr[i + 2];
      lines.push(a, b, b, c, c, a);
    }
    this.wireIndices = new Uint32Array(lines);
    this.wireGeo = new THREE.BufferGeometry();
    this.wireGeo.setAttribute("position", new THREE.BufferAttribute(this.displacedPositions, 3));
    this.wireGeo.setAttribute("color", new THREE.BufferAttribute(this.colors, 3));
    this.wireGeo.setIndex(new THREE.BufferAttribute(this.wireIndices, 1));
    this.wireMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
    });
    this.wire = new THREE.LineSegments(this.wireGeo, this.wireMat);
    this.scene.add(this.wire);
    this.currentDetail = clamped;
    baseGeo.dispose();
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, p: BioclastParams, f: AudioFeatures): void {
    // Lazy geometry rebuild when sphereDetail changes.
    if (this.currentDetail !== Math.round(p.sphereDetail)) {
      this.rebuildGeometry(p.sphereDetail);
    }

    this.time += dt;
    this.beatLevel = smoothBeat(this.beatLevel, f.beat, dt);
    // Continuous Y rotation if rotationSpeed > 0; cameraSpin still adds.
    this.rotation += dt * (0.05 * p.cameraSpin + p.sphereRotationSpeed * 0.6);
    this.wire.rotation.y = this.rotation;
    this.innerMesh.rotation.y = this.rotation;

    // Audio reactivity model — matches vizz's mental model:
    //   bass        → uniform radial pump (the whole sphere breathes)
    //   beat        → punchy radial expansion
    //   treble/mid  → spike amplitude and color flicker
    const baseR = Math.max(0.2, p.sphereSize);
    const audioPump = (f.bass * 0.5 * p.bassGravity + this.beatLevel * 0.7) * p.sphereVertexAudioMix;
    const spikeAmp =
      p.sphereDisplacement *
      (1 + f.treble * 1.4 * p.trebleShimmer + this.beatLevel * 0.6);
    const noiseScale = Math.max(0.3, p.sphereNoiseScale);
    const noiseTime = this.time * Math.max(0, p.sphereNoiseSpeed);
    const waveFreq = p.sphereWaveFrequency;
    const waveTime = this.time * p.sphereWaveSpeed;
    const noiseInt = p.sphereNoiseIntensity;
    const colorInt = Math.max(0.1, p.sphereColorIntensity);
    const smoothing = THREE.MathUtils.clamp(p.sphereMotionSmoothing, 0, 0.98);
    const lerpRate = 1 - Math.pow(smoothing, dt * 60); // smoothing-adjusted lerp

    // Accent color blending: when accentColor is set, blend per-vertex hue
    // from baseColor (poles) → accentColor (equator). When empty, use
    // lat/lon rainbow (vizz's rainbow look).
    const useAccent = p.sphereAccentColor && p.sphereAccentColor.length > 0;
    if (useAccent) this.accentColor.set(p.sphereAccentColor);
    const useExplicitBase = p.sphereBaseColor && p.sphereBaseColor.length > 0;
    if (useExplicitBase) this.baseColorRGB.set(p.sphereBaseColor);

    for (let v = 0; v < this.vertexCount; v++) {
      const i = v * 3;
      const bx = this.basePositions[i];
      const by = this.basePositions[i + 1];
      const bz = this.basePositions[i + 2];
      // Pseudo-3D noise: blend two 2D lookups along orthogonal projections.
      // Low noiseScale (vizz Config 28 = 3.1) creates large organic petals;
      // high noiseScale creates fuzzy spike texture.
      const n =
        (vnoise(bx * noiseScale + noiseTime, by * noiseScale) - 0.5) +
        (vnoise(by * noiseScale, bz * noiseScale + noiseTime * 1.3) - 0.5);
      // Wave term — sinusoidal latitude-based pulse.
      const wave = waveFreq > 0
        ? Math.sin(by * waveFreq * 6 + waveTime) * 0.3
        : 0;
      const targetR = baseR + audioPump + n * spikeAmp * noiseInt + wave * spikeAmp * 0.3;
      // Per-vertex temporal smoothing (matches vizz `smoothingFactor`).
      const cur = this.smoothedRadii[v] || targetR;
      const r = cur + (targetR - cur) * lerpRate;
      this.smoothedRadii[v] = r;
      this.displacedPositions[i] = bx * r;
      this.displacedPositions[i + 1] = by * r;
      this.displacedPositions[i + 2] = bz * r;

      // Color: rainbow lat/lon hue (vizz Config 28 default) OR base→accent
      // blend (vizz preset variants with both baseColor and accentColor set).
      if (useAccent && useExplicitBase) {
        // Latitude blend: pole = base, equator = accent. y in -1..1.
        const t = 1 - Math.abs(by);
        const r0 = this.baseColorRGB.r + (this.accentColor.r - this.baseColorRGB.r) * t;
        const g0 = this.baseColorRGB.g + (this.accentColor.g - this.baseColorRGB.g) * t;
        const b0 = this.baseColorRGB.b + (this.accentColor.b - this.baseColorRGB.b) * t;
        const lum = colorInt * (0.7 + Math.abs(n) * 0.6 + this.beatLevel * 0.3);
        this.colors[i] = Math.min(1, r0 * lum);
        this.colors[i + 1] = Math.min(1, g0 * lum);
        this.colors[i + 2] = Math.min(1, b0 * lum);
      } else {
        const hue = (this.vertexHues[v] + p.paletteHue / 360 + f.mid * 0.08) % 1;
        // colorIntensity drives lightness (not multiplier) so it stays in HSL space
        // instead of clipping to white. vizz `colorIntensity` 0..5 → lum 0.3..0.85.
        const lumBase = THREE.MathUtils.clamp(0.3 + colorInt * 0.11, 0.2, 0.85);
        const lum = THREE.MathUtils.clamp(
          lumBase + Math.abs(n) * 0.15 + this.beatLevel * 0.2,
          0,
          0.95,
        );
        this.tempColor.setHSL(hue, p.saturation, lum);
        this.colors[i] = this.tempColor.r;
        this.colors[i + 1] = this.tempColor.g;
        this.colors[i + 2] = this.tempColor.b;
      }
    }
    (this.wireGeo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.wireGeo.attributes.color as THREE.BufferAttribute).needsUpdate = true;

    // Inner solid: visibility, color, scale.
    this.innerMesh.visible = p.sphereSolidInterior;
    if (p.sphereSolidInterior) {
      const interior = useExplicitBase ? this.baseColorRGB : this.tempColor.set("#000000");
      this.innerMat.color.copy(interior);
      const coreScale = baseR * 0.6 * (1 + audioPump * 0.3);
      this.innerMesh.scale.setScalar(coreScale);
    }

    // Backdrop tint.
    const bgC = hsl(p.paletteHue, 0.4, 0.1);
    this.backdrop.r = Math.round(bgC.r * 30 + this.beatLevel * 30);
    this.backdrop.g = Math.round(bgC.g * 30);
    this.backdrop.b = Math.round(bgC.b * 50);
    this.backdrop.alpha = 0.25 + f.bass * 0.3;
  }

  dispose(): void {
    this.wireGeo.dispose();
    this.wireMat.dispose();
    this.innerGeo.dispose();
    this.innerMat.dispose();
  }
}

// ---------------------------------------------------------------------------
// GalaxyScene -- Wall Lab-inspired particle river, integrated as a normal
// WebGL alt scene so it can use the creator's post-FX, capture, and UI stack.
// ---------------------------------------------------------------------------
export class GalaxyScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 3, g: 8, b: 22, alpha: 0.42, blurPx: 44 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private readonly geometry = new THREE.BufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private readonly points: THREE.Points;
  private time = 0;
  private dropPulse = 0;

  constructor(width: number, height: number) {
    this.camera = new THREE.PerspectiveCamera(62, width / height, 0.1, 3000);
    this.camera.position.set(0, 180, 720);
    this.camera.lookAt(0, 0, 0);

    const count = 120000;
    const radius = new Float32Array(count);
    const angle = new Float32Array(count);
    const lane = new Float32Array(count);
    const seed = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const t = i / count;
      radius[i] = Math.pow(t, 0.58);
      angle[i] = i * 2.399963229728653 + Math.random() * 0.28;
      lane[i] = Math.random() * 2 - 1;
      seed[i] = Math.random();
    }

    this.geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
    this.geometry.setAttribute("aRadius", new THREE.BufferAttribute(radius, 1));
    this.geometry.setAttribute("aAngle", new THREE.BufferAttribute(angle, 1));
    this.geometry.setAttribute("aLane", new THREE.BufferAttribute(lane, 1));
    this.geometry.setAttribute("aSeed", new THREE.BufferAttribute(seed, 1));

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uBass: { value: 0 },
        uMid: { value: 0 },
        uTreble: { value: 0 },
        uBeat: { value: 0 },
        uDrop: { value: 0 },
        uHue: { value: 0.55 },
        uRadius: { value: 900 },
        uPointSize: { value: 2.2 },
        uFlow: { value: 1 },
        uOpacity: { value: 0.82 },
      },
      vertexShader: `
        attribute float aRadius;
        attribute float aAngle;
        attribute float aLane;
        attribute float aSeed;
        uniform float uTime;
        uniform float uBass;
        uniform float uMid;
        uniform float uTreble;
        uniform float uBeat;
        uniform float uDrop;
        uniform float uRadius;
        uniform float uPointSize;
        uniform float uFlow;
        varying float vEnergy;
        varying float vSeed;
        varying float vRadius;

        void main() {
          float r = pow(aRadius, 1.18) * uRadius;
          float spiral = aAngle + uTime * (0.06 + uFlow * 0.18) * (1.4 - aRadius);
          float cymatic = sin(aRadius * 42.0 - uTime * 6.0) * uBeat * (22.0 + uBass * 58.0);
          float laneWave = sin(aAngle * 3.0 + uTime * 1.4 + aSeed * 8.0);
          float curl = sin(spiral * 2.0 + uTime * 0.8 + aLane * 3.0) * uMid * 42.0;
          float burst = uDrop * (1.0 - aRadius) * 180.0;
          r += cymatic + burst;

          vec3 pos;
          pos.x = cos(spiral) * r + aLane * (18.0 + uMid * 55.0) + curl;
          pos.z = sin(spiral) * r + laneWave * (26.0 + uBass * 50.0);
          pos.y = (aLane * 70.0 + laneWave * 34.0) * (0.45 + aRadius) + uTreble * 120.0 * (aSeed - 0.5);

          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          float energy = clamp((uBass + uMid + uTreble) / 3.0 + uBeat * 0.5 + uDrop * 0.35, 0.0, 2.0);
          gl_PointSize = uPointSize * (1.0 + energy * 1.6 + (1.0 - aRadius) * 0.8) * (520.0 / max(120.0, -mvPosition.z));
          gl_Position = projectionMatrix * mvPosition;
          vEnergy = energy;
          vSeed = aSeed;
          vRadius = aRadius;
        }
      `,
      fragmentShader: `
        uniform float uHue;
        uniform float uOpacity;
        varying float vEnergy;
        varying float vSeed;
        varying float vRadius;

        vec3 hsl2rgb(vec3 c) {
          vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0,4.0,2.0),6.0)-3.0)-1.0,0.0,1.0);
          return c.z + c.y * (rgb - 0.5) * (1.0 - abs(2.0 * c.z - 1.0));
        }

        void main() {
          vec2 uv = gl_PointCoord - 0.5;
          float d = dot(uv, uv);
          float alpha = smoothstep(0.25, 0.02, d) * uOpacity * (0.42 + vEnergy * 0.58);
          if (alpha < 0.01) discard;
          float hue = fract(uHue + vRadius * 0.18 + vSeed * 0.08 + vEnergy * 0.04);
          vec3 col = hsl2rgb(vec3(hue, 0.82, 0.58 + vEnergy * 0.12));
          gl_FragColor = vec4(col, alpha);
        }
      `,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.scene.add(this.points);
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, p: BioclastParams, f: AudioFeatures): void {
    const level = Math.max(f.level, (f.bass + f.mid + f.treble) / 3);
    this.time += dt * (0.35 + p.flowSpeed * 0.55 + level * 0.55);
    this.beatLevel = smoothBeat(this.beatLevel, f.beat, dt);
    this.dropPulse = Math.max(this.dropPulse * Math.exp(-dt * 2.8), f.beat * Math.max(0.4, f.bass));

    this.material.uniforms.uTime.value = this.time;
    this.material.uniforms.uBass.value = f.bass * Math.max(0.1, p.bassGravity);
    this.material.uniforms.uMid.value = f.mid * Math.max(0.1, p.midCurl);
    this.material.uniforms.uTreble.value = f.treble * Math.max(0.1, p.trebleShimmer);
    this.material.uniforms.uBeat.value = this.beatLevel;
    this.material.uniforms.uDrop.value = this.dropPulse;
    this.material.uniforms.uHue.value = ((p.paletteHue % 360) + 360) % 360 / 360;
    this.material.uniforms.uRadius.value = 560 + p.cameraZoom * 180;
    this.material.uniforms.uPointSize.value = 1.5 + p.starSize * 3.2;
    this.material.uniforms.uFlow.value = p.flowSpeed;
    this.material.uniforms.uOpacity.value = Math.max(0.18, Math.min(1, p.opacity));

    this.points.rotation.y = Math.sin(this.time * 0.06) * 0.12;
    this.points.rotation.x = -0.12 + Math.sin(this.time * 0.04) * 0.04;

    this.backdrop.r = Math.round(4 + f.bass * 18 + this.dropPulse * 28);
    this.backdrop.g = Math.round(8 + f.mid * 20 + this.beatLevel * 16);
    this.backdrop.b = Math.round(22 + f.treble * 42 + level * 16);
    this.backdrop.alpha = 0.28 + level * 0.32;
    this.backdrop.blurPx = 34 + level * 38;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

type MathParticleMode = "superform" | "spiralVortex" | "hopfFibration" | "fibonacciSequence" | "radialPulse";

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

function superRadius(angle: number, symmetry: number, n1: number, n2: number, n3: number): number {
  const cosineTerm = Math.abs(Math.cos(symmetry * angle * 0.25));
  const sineTerm = Math.abs(Math.sin(symmetry * angle * 0.25));
  return Math.pow(Math.max(0.0001, Math.pow(cosineTerm, n2) + Math.pow(sineTerm, n3)), -1 / Math.max(0.15, n1));
}

function seededUnit(index: number, salt: number): number {
  const value = Math.sin(index * 127.1 + salt * 311.7) * 43758.5453123;
  return value - Math.floor(value);
}

// MathParticleScene restores the production-only native geometry scenes that
// existed in the good dd80c00f Pages deployment: Superform, Spiral Vortex,
// Hopf Fibration, and Fibonacci Sequence.
export class MathParticleScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(58, 1, 0.1, 240);
  readonly backdrop = { r: 3, g: 6, b: 16, alpha: 0.44, blurPx: 42 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private time = 0;
  private readonly count: number;
  private readonly geometry = new THREE.BufferGeometry();
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly seeds: Float32Array;
  private readonly material: THREE.PointsMaterial;
  private readonly points: THREE.Points;
  private readonly colorScratch = new THREE.Color();
  private readonly mode: MathParticleMode;

  constructor(w: number, h: number, mode: MathParticleMode) {
    this.mode = mode;
    this.count = mode === "hopfFibration" ? 16800 : mode === "fibonacciSequence" ? 15000 : mode === "radialPulse" ? 15600 : 14000;
    this.positions = new Float32Array(this.count * 3);
    this.colors = new Float32Array(this.count * 3);
    this.seeds = new Float32Array(this.count * 2);

    for (let index = 0; index < this.count; index++) {
      const seedOffset = index * 2;
      this.seeds[seedOffset] = seededUnit(index, 4.17);
      this.seeds[seedOffset + 1] = seededUnit(index, 9.71);
    }

    this.geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute("color", new THREE.BufferAttribute(this.colors, 3));
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 48);

    this.material = new THREE.PointsMaterial({
      size: 0.14,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.92,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.scene.add(this.points);
    this.camera.position.set(0, 5, 68);
    this.camera.lookAt(0, 0, 0);
    this.setSize(w, h);
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / Math.max(1, h);
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    this.time += dt * (0.35 + params.flowSpeed * 0.35);
    this.beatLevel = smoothBeat(this.beatLevel, features.beat, dt);

    const bass = features.bass * params.bassGravity;
    const mid = features.mid * params.midCurl;
    const treble = features.treble * params.trebleShimmer;
    const energy = THREE.MathUtils.clamp((bass * 0.45 + mid * 0.35 + treble * 0.2) * params.intensity, 0, 2.4);
    const pulse = 1 + this.beatLevel * 0.1 + bass * 0.035;
    const hueBase = ((params.paletteHue % 360) + 360) % 360;
    const globalYaw = this.time * (0.12 + params.cameraSpin * 0.06);
    const yawCosine = Math.cos(globalYaw);
    const yawSine = Math.sin(globalYaw);
    const globalPitch = Math.sin(this.time * 0.17) * 0.14 + (params.cameraTilt - 0.5) * 0.35;
    const pitchCosine = Math.cos(globalPitch);
    const pitchSine = Math.sin(globalPitch);
    const countMinusOne = Math.max(1, this.count - 1);

    for (let index = 0; index < this.count; index++) {
      const progress = index / countMinusOne;
      const seedOffset = index * 2;
      const seedA = this.seeds[seedOffset];
      const seedB = this.seeds[seedOffset + 1];
      let positionX = 0;
      let positionY = 0;
      let positionZ = 0;
      let band = 0;

      if (this.mode === "superform") {
        const baseY = 1 - 2 * progress;
        const basePhi = Math.acos(THREE.MathUtils.clamp(baseY, -0.98, 0.98));
        const advect = this.time * (0.42 + params.flowSpeed * 0.18 + params.cameraSpin * 0.05);
        const lane = seedA * Math.PI * 2;
        const phi = THREE.MathUtils.clamp(
          basePhi + 0.22 * Math.sin(advect * 0.55 + lane + progress * Math.PI * 2),
          0.035,
          Math.PI - 0.035,
        );
        const theta = index * GOLDEN_ANGLE + advect * (0.75 + seedB * 0.45) + 0.22 * Math.sin(phi * 3 - advect * 0.8 + lane);
        const symmetry = 4 + params.cymaticStrength * 0.75 + 0.45 * Math.sin(this.time * 0.13 + seedB * Math.PI * 2);
        const n1 = 0.92 + energy * 0.18 + 0.08 * Math.sin(this.time * 0.17 + seedA * Math.PI * 2);
        const n2 = 1.45 + 0.25 * Math.sin(this.time * 0.11 + seedB * 5);
        const n3 = 1.45 + 0.25 * Math.cos(this.time * 0.1 + seedA * 4);
        const azimuthRadius = superRadius(theta, symmetry, n1, n2, n3);
        const latitudeRadius = superRadius(phi - Math.PI * 0.5, 2 + params.cymaticStrength * 0.1, n1, 1.6, 1.6);
        const shape = THREE.MathUtils.lerp(1, azimuthRadius * latitudeRadius, THREE.MathUtils.clamp(0.16 + energy * 0.11, 0, 0.52));
        const stream = Math.sin(theta * 2 + phi * 5 - this.time * (1.1 + params.flowSpeed * 0.45) + lane);
        const fine = Math.sin(theta * 7 - phi * 2 - this.time * 1.7 + seedB * Math.PI * 2);
        const radius = 19.5 * shape * pulse;
        const sinPhi = Math.sin(phi);
        positionX = Math.cos(theta) * sinPhi * radius - Math.sin(theta) * stream * energy * 0.6;
        positionY = Math.cos(phi) * radius + fine * energy * 0.45;
        positionZ = Math.sin(theta) * sinPhi * radius + Math.cos(theta) * stream * energy * 0.6;
        band = 0.5 + 0.5 * stream;
      } else if (this.mode === "hopfFibration") {
        const fiberCount = 112;
        const pointsPerFiber = Math.floor(this.count / fiberCount);
        const fiberIndex = Math.floor(index / pointsPerFiber);
        const pointInFiber = index - fiberIndex * pointsPerFiber;
        const fiberProgress = fiberIndex / Math.max(1, fiberCount - 1);
        const ringProgress = pointInFiber / Math.max(1, pointsPerFiber - 1);
        const baseY = 1 - 2 * fiberProgress;
        const baseRadius = Math.sqrt(Math.max(0, 1 - baseY * baseY));
        const baseTheta = fiberIndex * GOLDEN_ANGLE + this.time * 0.09;
        const directionX = Math.cos(baseTheta) * baseRadius;
        const directionY = baseY;
        const directionZ = Math.sin(baseTheta) * baseRadius;
        const tangentX = -Math.sin(baseTheta);
        const tangentY = 0;
        const tangentZ = Math.cos(baseTheta);
        const bitangentX = directionY * tangentZ - directionZ * tangentY;
        const bitangentY = directionZ * tangentX - directionX * tangentZ;
        const bitangentZ = directionX * tangentY - directionY * tangentX;
        const fiberAngle = ringProgress * Math.PI * 2 + this.time * (0.45 + seedA * 0.12) + fiberProgress * Math.PI * 4;
        const centerScale = 11.5 + Math.sin(fiberProgress * Math.PI * 6 + this.time) * 1.4;
        const ringRadius = 5.4 + seedB * 2.6 + energy * 1.1;
        positionX = directionX * centerScale + (Math.cos(fiberAngle) * tangentX + Math.sin(fiberAngle) * bitangentX) * ringRadius;
        positionY = directionY * centerScale + (Math.cos(fiberAngle) * tangentY + Math.sin(fiberAngle) * bitangentY) * ringRadius;
        positionZ = directionZ * centerScale + (Math.cos(fiberAngle) * tangentZ + Math.sin(fiberAngle) * bitangentZ) * ringRadius;
        band = fiberProgress;
      } else if (this.mode === "fibonacciSequence") {
        const spiralTurn = index * GOLDEN_ANGLE + this.time * (0.34 + params.flowSpeed * 0.16);
        const discRadius = Math.sqrt(progress) * (22 + energy * 2.2);
        const spiralLift = Math.sin(progress * Math.PI * 9 - this.time * 1.4 + seedA * Math.PI * 2) * (1.4 + energy * 0.9);
        const bowl = (progress - 0.5) * (progress - 0.5) * -9 + 2.2;
        const nautilus = Math.exp(progress * 1.45) * 0.9;
        const blend = THREE.MathUtils.clamp(0.35 + mid * 0.08, 0.35, 0.78);
        const sphereY = 1 - 2 * progress;
        const sphereRadius = Math.sqrt(Math.max(0, 1 - sphereY * sphereY)) * 17;
        const discX = Math.cos(spiralTurn) * (discRadius + nautilus);
        const discZ = Math.sin(spiralTurn) * (discRadius + nautilus);
        const sphereX = Math.cos(spiralTurn) * sphereRadius;
        const sphereZ = Math.sin(spiralTurn) * sphereRadius;
        positionX = THREE.MathUtils.lerp(discX, sphereX, blend) * pulse;
        positionY = THREE.MathUtils.lerp(bowl + spiralLift, sphereY * 15, blend) + Math.sin(spiralTurn * 3 + this.time) * energy * 0.35;
        positionZ = THREE.MathUtils.lerp(discZ, sphereZ, blend) * pulse;
        band = (spiralTurn / (Math.PI * 2)) % 1;
      } else if (this.mode === "radialPulse") {
        // Concentric latitude rings forming a breathing teardrop/onion silhouette
        // (matches the original PulseFX "Radial Pulse Field" reference frames).
        const ringCount = 42;
        const pointsPerRing = Math.max(1, Math.floor(this.count / ringCount));
        const ringIndex = Math.floor(index / pointsPerRing);
        const pointInRing = index - ringIndex * pointsPerRing;
        const ringProgress = ringIndex / Math.max(1, ringCount - 1);
        const phi = THREE.MathUtils.clamp(ringProgress * Math.PI, 0.04, Math.PI - 0.04);
        // Asymmetric teardrop: bulb at lower phi, pinched cusp toward the top.
        const tearShape = Math.pow(Math.sin(phi), 1.05) * (0.78 + 0.42 * Math.cos(phi * 0.5));
        const breath =
          1 +
          Math.sin(this.time * 1.25 + ringProgress * Math.PI * 4) * 0.045 +
          this.beatLevel * 0.14 +
          energy * 0.05;
        const ringRadius = 17.2 * tearShape * breath;
        const theta = (pointInRing / pointsPerRing) * Math.PI * 2 + this.time * (0.18 + seedA * 0.07);
        // Subtle radial flutter so each ring breathes independently.
        const flutter =
          Math.sin(phi * 7 - this.time * 1.6 + seedB * Math.PI * 2) * (0.18 + energy * 0.12);
        positionX = Math.cos(theta) * (ringRadius + flutter);
        positionZ = Math.sin(theta) * (ringRadius + flutter);
        // Cusp up: negate cos(phi) and bias upward so it reads as a teardrop standing on its base.
        positionY = -Math.cos(phi) * 19 * breath + 1.6;
        band = ringProgress;
      } else {
        const inner = index % 8 === 7;
        if (inner) {
          const innerIndex = Math.floor(index / 8);
          const innerCount = Math.max(1, Math.floor(this.count / 8));
          const theta = innerIndex / innerCount * Math.PI * 2 + this.time * 0.22;
          const tube = seedB * Math.PI * 2;
          const major = 8.4 + this.beatLevel * 0.7;
          const minor = 1.2 + energy * 0.18;
          positionX = (major + minor * Math.cos(tube)) * Math.cos(theta);
          positionZ = (major + minor * Math.cos(tube)) * Math.sin(theta);
          positionY = minor * Math.sin(tube) * 0.45;
          band = 0.92;
        } else {
          const theta = progress * Math.PI * 2 * 6 + seedA * Math.PI * 2 + this.time * (0.58 + params.flowSpeed * 0.22);
          const tube = seedB * Math.PI * 2 + this.time * 0.18;
          const flare = Math.sin(progress * Math.PI);
          const major = 12 + flare * 10 + energy * 0.9;
          const minor = 3.4 + Math.sin(progress * Math.PI * 4 + this.time) * 1.2;
          positionX = (major + minor * Math.cos(tube)) * Math.cos(theta);
          positionZ = (major + minor * Math.cos(tube)) * Math.sin(theta);
          positionY = (progress - 0.5) * 24 + Math.sin(theta * 0.5 + this.time) * 2.5;
          band = 0.15 + flare * 0.55;
        }
      }

      const pitchedY = positionY * pitchCosine - positionZ * pitchSine;
      const pitchedZ = positionY * pitchSine + positionZ * pitchCosine;
      const rotatedX = positionX * yawCosine - pitchedZ * yawSine;
      const rotatedZ = positionX * yawSine + pitchedZ * yawCosine;
      const positionOffset = index * 3;
      this.positions[positionOffset] = rotatedX;
      this.positions[positionOffset + 1] = pitchedY;
      this.positions[positionOffset + 2] = rotatedZ;

      const hue = hueBase + band * 90 + energy * 10 + (this.mode === "fibonacciSequence" ? 34 : 0);
      const saturation = THREE.MathUtils.clamp(params.saturation + 0.08 + treble * 0.04, 0, 1);
      const lightness = THREE.MathUtils.clamp(0.56 + band * 0.24 + energy * 0.08, 0.5, 0.9);
      const colorLift = THREE.MathUtils.clamp(1.45 + energy * 0.22 + this.beatLevel * 0.18, 1.25, 2.15);
      this.colorScratch.setHSL(((hue % 360) + 360) % 360 / 360, saturation, lightness);
      this.colors[positionOffset] = this.colorScratch.r * colorLift;
      this.colors[positionOffset + 1] = this.colorScratch.g * colorLift;
      this.colors[positionOffset + 2] = this.colorScratch.b * colorLift;
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;
    this.material.size = 0.08 + params.starSize * 0.62 + this.beatLevel * 0.08;
    this.material.opacity = THREE.MathUtils.clamp(params.opacity * (0.82 + energy * 0.08), 0.48, 1);

    this.backdrop.r = Math.round(4 + bass * 10 + this.beatLevel * 16);
    this.backdrop.g = Math.round(6 + mid * 16 + energy * 5);
    this.backdrop.b = Math.round(18 + treble * 26 + energy * 12);
    this.backdrop.alpha = 0.32 + THREE.MathUtils.clamp(energy * 0.18, 0, 0.34);
    this.backdrop.blurPx = 36 + energy * 18;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}


// ---------------------------------------------------------------------------
// HopfFibrationPointsScene — port of the standalone PulseFX
// "Hopf Fibration · S¹ → S³ → S²" reference (pulsefx-hopf-fibration.html),
// rendered as additive points sampled along each Hopf fiber rather than as
// TubeGeometry meshes. Park parameterization with continuous SO(4) (y,z)
// rotation gives the non-trivial-topology flow. Exposed as the `hopfTubes`
// AltSceneType so it can be picked alongside the live MathParticleScene
// hopfFibration mode rather than replacing it.
// ---------------------------------------------------------------------------
export class HopfFibrationPointsScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(45, 1, 0.05, 800);
  readonly backdrop = { r: 0, g: 0, b: 6, alpha: 0.5, blurPx: 32 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private readonly LATS = 7;
  private readonly LONS = 18;
  private readonly FIBER_SAMPLES = 140;
  private readonly C_MAX = 0.78;
  private readonly root = new THREE.Group();
  private readonly fiberSeeds: { a: number; b: number; c: number; hue: number }[] = [];
  private readonly geometry = new THREE.BufferGeometry();
  private positionsAttr!: THREE.BufferAttribute;
  private colorsAttr!: THREE.BufferAttribute;
  private points!: THREE.Points;
  private mat!: THREE.PointsMaterial;
  private time = 0;
  private beta = 0;

  constructor(w: number, h: number) {
    this.scene.background = new THREE.Color(0x000004);
    this.scene.fog = new THREE.FogExp2(0x000004, 0.01);
    this.scene.add(this.root);
    this.buildFibers();
    this.camera.position.set(0, 0, 9.5);
    this.camera.lookAt(0, 0, 0);
    this.setSize(w, h);
  }

  // Park parameterization: for base point (a,b,c) on S², the Hopf fiber is
  // the circle in S³ ⊂ R⁴ given by t ↦ (x,y,z,w); stereographic projection
  // from (0,0,0,1) drops it to R³. `beta` rotates the (y,z) plane in R⁴
  // before projection (continuous SO(4) re-fibration).
  private writeFiberPoints(
    a: number,
    b: number,
    c: number,
    samples: number,
    beta: number,
    out: Float32Array,
    offset: number,
  ): void {
    const TWO_PI = Math.PI * 2;
    const denom = Math.sqrt(2 * (1 + c));
    const cb = Math.cos(beta);
    const sb = Math.sin(beta);
    for (let i = 0; i < samples; i++) {
      const t = (i / samples) * TWO_PI;
      const ct = Math.cos(t);
      const st = Math.sin(t);
      const x = ((1 + c) * ct) / denom;
      let y = (a * st - b * ct) / denom;
      let z = (a * ct + b * st) / denom;
      const w4 = ((1 + c) * st) / denom;
      const yn = cb * y - sb * z;
      const zn = sb * y + cb * z;
      y = yn;
      z = zn;
      const k = 1.0 / (1.0 - w4);
      const idx = offset + i * 3;
      out[idx] = x * k;
      out[idx + 1] = y * k;
      out[idx + 2] = z * k;
    }
  }

  private buildFibers(): void {
    const TWO_PI = Math.PI * 2;
    const totalPoints = this.LATS * this.LONS * this.FIBER_SAMPLES;
    const positions = new Float32Array(totalPoints * 3);
    const colors = new Float32Array(totalPoints * 3);
    let off = 0;
    for (let li = 0; li < this.LATS; li++) {
      const u = li / Math.max(1, this.LATS - 1);
      const c = -this.C_MAX + u * (2 * this.C_MAX);
      const sinPhi = Math.sqrt(Math.max(0, 1 - c * c));
      const hueLat = (1 - u) * 0.78;
      for (let lj = 0; lj < this.LONS; lj++) {
        const theta = (lj / this.LONS) * TWO_PI;
        const a = sinPhi * Math.cos(theta);
        const b = sinPhi * Math.sin(theta);
        const hueRing = (theta / TWO_PI) * 0.1;
        const hue = (hueLat + hueRing) % 1;
        this.writeFiberPoints(a, b, c, this.FIBER_SAMPLES, 0, positions, off * 3);
        const color = new THREE.Color().setHSL(hue, 0.85, 0.55);
        for (let i = 0; i < this.FIBER_SAMPLES; i++) {
          const ci = (off + i) * 3;
          colors[ci] = color.r;
          colors[ci + 1] = color.g;
          colors[ci + 2] = color.b;
        }
        this.fiberSeeds.push({ a, b, c, hue });
        off += this.FIBER_SAMPLES;
      }
    }
    this.positionsAttr = new THREE.BufferAttribute(positions, 3);
    this.positionsAttr.setUsage(THREE.DynamicDrawUsage);
    this.colorsAttr = new THREE.BufferAttribute(colors, 3);
    this.colorsAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute("position", this.positionsAttr);
    this.geometry.setAttribute("color", this.colorsAttr);
    this.mat = new THREE.PointsMaterial({
      size: 0.04,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(this.geometry, this.mat);
    this.points.frustumCulled = false;
    this.root.add(this.points);
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / Math.max(1, h);
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    this.time += dt;
    this.beatLevel = smoothBeat(this.beatLevel, features.beat, dt);
    const bass = features.bass * params.bassGravity;
    const mid = features.mid * params.midCurl;
    const treble = features.treble * params.trebleShimmer;
    const energy = THREE.MathUtils.clamp(
      (bass * 0.45 + mid * 0.35 + treble * 0.2) * params.intensity,
      0,
      2.4,
    );

    this.beta += dt * (0.32 + params.flowSpeed * 0.5 + mid * 0.35);
    this.root.rotation.y += dt * (0.32 + params.cameraSpin * 0.6);
    this.root.rotation.x =
      Math.sin(this.time * 0.13) * 0.12 + (params.cameraTilt - 0.5) * 0.4;

    const positions = this.positionsAttr.array as Float32Array;
    let off = 0;
    for (const f of this.fiberSeeds) {
      this.writeFiberPoints(f.a, f.b, f.c, this.FIBER_SAMPLES, this.beta, positions, off * 3);
      off += this.FIBER_SAMPLES;
    }
    this.positionsAttr.needsUpdate = true;

    const sizeBase = THREE.MathUtils.clamp(
      0.028 + params.starSize * 0.06 + this.beatLevel * 0.018 + energy * 0.012,
      0.012,
      0.12,
    );
    this.mat.size = sizeBase;
    this.mat.opacity = THREE.MathUtils.clamp(
      (0.78 + energy * 0.1 + this.beatLevel * 0.1) * Math.max(0.55, params.opacity),
      0.5,
      1,
    );

    const hueBase = (((params.paletteHue % 360) + 360) % 360) / 360;
    const sat = THREE.MathUtils.clamp(params.saturation * 0.9 + 0.05, 0.2, 1);
    const lit = THREE.MathUtils.clamp(0.62 + this.beatLevel * 0.12 + energy * 0.06, 0.55, 0.96);
    const colorLift = THREE.MathUtils.clamp(1.45 + energy * 0.22 + this.beatLevel * 0.18, 1.25, 2.2);
    const colors = this.colorsAttr.array as Float32Array;
    const tmp = new THREE.Color();
    off = 0;
    for (const f of this.fiberSeeds) {
      const hueShifted = (hueBase + f.hue + energy * 0.05) % 1;
      tmp.setHSL(hueShifted, sat, lit);
      for (let i = 0; i < this.FIBER_SAMPLES; i++) {
        const ci = (off + i) * 3;
        colors[ci] = tmp.r * colorLift;
        colors[ci + 1] = tmp.g * colorLift;
        colors[ci + 2] = tmp.b * colorLift;
      }
      off += this.FIBER_SAMPLES;
    }
    this.colorsAttr.needsUpdate = true;

    this.backdrop.r = Math.round(2 + bass * 8 + this.beatLevel * 12);
    this.backdrop.g = Math.round(4 + mid * 12 + energy * 5);
    this.backdrop.b = Math.round(14 + treble * 22 + energy * 10);
    this.backdrop.alpha = 0.34 + THREE.MathUtils.clamp(energy * 0.18, 0, 0.36);
    this.backdrop.blurPx = 30 + energy * 18;
  }

  dispose(): void {
    this.geometry.dispose();
    this.mat.dispose();
    this.root.remove(this.points);
    this.fiberSeeds.length = 0;
  }
}


// ---------------------------------------------------------------------------
// SuperformGPUScene — GPGPU 250k-particle field ported from the
// song-galaxy prototype. Replaces the legacy CPU MathParticleScene for the
// math-particle archetypes. Particles live in a ping-pong RGBA32F texture
// pair; positions advance via a critically-damped spring toward the
// current formation's GLSL target.
// ---------------------------------------------------------------------------
import { ParticleSystemGL } from "../engine-webgl/ParticleSystemGL";

// Formation IDs come straight from `engine-webgl/formationsGLSL.ts` so the
// alt-scene → formation mapping stays a small, single-file table inside
// `createAltScene` below.

export class SuperformGPUScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(45, 1, 0.1, 600);
  readonly backdrop = { r: 3, g: 6, b: 16, alpha: 0.44, blurPx: 42 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private particles: ParticleSystemGL | null = null;
  private viewportPx = 800;
  private dpr = 1;
  private time = 0;
  private lastDt = 1 / 60;
  private formationId: number;
  private bootstrapped = false;

  constructor(w: number, h: number, formationId: number) {
    this.formationId = formationId;
    this.camera.position.set(0, 0, 26);
    this.camera.lookAt(0, 0, 0);
    this.viewportPx = h;
    this.setSize(w, h);
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = w / Math.max(1, h);
    this.camera.updateProjectionMatrix();
    this.viewportPx = h;
    if (this.particles) this.particles.setViewportHeight(h * this.dpr);
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    this.lastDt = dt;
    this.time += dt;
    this.beatLevel = smoothBeat(this.beatLevel, features.beat, dt);

    if (this.particles) {
      this.particles.setBaseRadius(7.0 + params.starSize * 6.0 + params.fieldTightness * 2.0);
      this.particles.setPointSize(0.012 + params.starSize * 0.035);
      this.particles.setAlpha(THREE.MathUtils.clamp(params.opacity * 0.34 + 0.08, 0.14, 0.52));
      this.particles.setBrightness(THREE.MathUtils.clamp(0.42 + params.intensity * 0.32, 0.32, 0.9));
      this.particles.setAudio(
        THREE.MathUtils.clamp(features.bass * params.bassGravity, 0, 1.6),
        THREE.MathUtils.clamp(features.mid * params.midCurl, 0, 1.6),
        THREE.MathUtils.clamp(features.treble * params.trebleShimmer, 0, 1.6),
        this.beatLevel,
      );
      this.particles.advanceCrossfade(dt);
    }

    // Orbital camera driven by params.
    const yaw = this.time * (0.08 + params.cameraSpin * 0.06) + params.cameraOrbit;
    const tilt = (params.cameraTilt - 0.5) * 0.9;
    const zoom = THREE.MathUtils.clamp(params.cameraZoom, 0.4, 3.0);
    const radius = 30 / zoom;
    const eyeY = Math.sin(tilt) * radius;
    const horizR = Math.cos(tilt) * radius;
    this.camera.position.set(Math.sin(yaw) * horizR, eyeY, Math.cos(yaw) * horizR);
    this.camera.lookAt(0, 0, 0);

    const energy = THREE.MathUtils.clamp(
      (features.bass * 0.4 + features.mid * 0.3 + features.treble * 0.3) * params.intensity,
      0,
      2,
    );
    this.backdrop.r = Math.round(4 + features.bass * 10 + this.beatLevel * 16);
    this.backdrop.g = Math.round(6 + features.mid * 16 + energy * 5);
    this.backdrop.b = Math.round(18 + features.treble * 26 + energy * 12);
    this.backdrop.alpha = 0.32 + THREE.MathUtils.clamp(energy * 0.18, 0, 0.34);
    this.backdrop.blurPx = 36 + energy * 18;
  }

  prerender(renderer: THREE.WebGLRenderer, dt: number, time: number): void {
    if (!this.bootstrapped) {
      this.bootstrapped = true;
      this.dpr = renderer.getPixelRatio();
      const isMobile = typeof window !== "undefined"
        ? (window.matchMedia?.("(max-width: 720px)").matches ?? false)
        : false;
      const count = isMobile ? 60_000 : 220_000;
      this.particles = new ParticleSystemGL(renderer, count);
      this.particles.init();
      this.particles.setViewportHeight(this.viewportPx * this.dpr);
      this.particles.setFormation(this.formationId, 0.001);
      this.scene.add(this.particles.mesh);
    }
    this.particles?.step(dt, time);
    void this.lastDt;
  }

  dispose(): void {
    this.particles?.dispose();
    this.particles = null;
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh & THREE.Points;
      if (mesh.geometry && typeof mesh.geometry.dispose === "function") {
        // Guard: ParticleSystemGL already disposed its own geometry above.
        if (mesh !== this.particles?.mesh) mesh.geometry.dispose();
      }
      const mat = (mesh.material as THREE.Material | undefined);
      if (mat && typeof mat.dispose === "function") {
        if (mesh !== this.particles?.mesh) mat.dispose();
      }
    });
    this.scene.clear();
  }
}


export type AltSceneType =
  | "meshGrid"
  | "wavyForm"
  | "dotGrid"
  | "frequencyBars"
  | "texturedSphere"
  | "galaxy"
  | "superform"
  | "spiralVortex"
  | "hopfFibration"
  | "fibonacciSequence"
  | "radialPulse"
  | "sineSurge"
  | "stellarator"
  | "plasmaSheet"
  | "noiseBloom"
  | "harmonicShell"
  | "strangeAttractor"
  | "lorenzDrift"
  | "organicBreath"
  | "helicalTwist"
  // Hopf as Park-parameterized fiber strands (HopfFibrationPointsScene).
  // Distinct from the bare `hopfFibration` key which renders the live math shader.
  | "hopfTubes"
  // Live shader aliases exposed under explicit `math*` keys for stable curated routing.
  | "mathSuperform"
  | "mathFibonacci"
  | "mathRadialPulse"
  | "mathCurlPlasma"
  | "mathHopfCathedral"
  | "mathKnotChoir"
  | "mathLorenzEmber"
  | "mathPhyllotacticGalaxy"
  | "mathReactionSkin"
  | "mathSphericalHarmonicShell"
  | "mathSongPlanetCore"
  // Explicit GPGPU 250k-particle routes for curated (GPU) variant pads.
  // These never collide with the live bare keys, which stay on LiveMathParticleScene.
  | "superformGPU"
  | "hopfFibrationGPU"
  | "fibonacciSequenceGPU"
  | "radialPulseGPU";

export const ALT_SCENE_LABELS: Record<AltSceneType, string> = {
  meshGrid: "Mesh Grid",
  wavyForm: "Wavy Form",
  dotGrid: "Dot Grid",
  frequencyBars: "Frequency Bars",
  texturedSphere: "Textured Sphere",
  galaxy: "Galaxy",
  superform: "Superform",
  spiralVortex: "Spiral Vortex",
  hopfFibration: "Hopf Fibration",
  fibonacciSequence: "Fibonacci Sequence",
  radialPulse: "Radial Pulse",
  sineSurge: "Sine Surge",
  stellarator: "Stellarator",
  plasmaSheet: "Plasma Sheet",
  noiseBloom: "Noise Bloom",
  harmonicShell: "Harmonic Shell",
  strangeAttractor: "Strange Attractor",
  lorenzDrift: "Lorenz Drift",
  organicBreath: "Organic Breath",
  helicalTwist: "Helical Twist",
  hopfTubes: "Hopf Fibration (Linked Strands)",
  mathSuperform: "Superform (Live)",
  mathFibonacci: "Fibonacci Sequence (Live)",
  mathRadialPulse: "Radial Pulse (Live)",
  mathCurlPlasma: "Curl Plasma",
  mathHopfCathedral: "Hopf Cathedral",
  mathKnotChoir: "Knot Choir",
  mathLorenzEmber: "Lorenz Ember",
  mathPhyllotacticGalaxy: "Phyllotactic Galaxy",
  mathReactionSkin: "Reaction Skin",
  mathSphericalHarmonicShell: "Spherical Harmonic Shell",
  mathSongPlanetCore: "Song Planet Core",
  superformGPU: "Superform (GPU)",
  hopfFibrationGPU: "Hopf Fibration (GPU)",
  fibonacciSequenceGPU: "Fibonacci Sequence (GPU)",
  radialPulseGPU: "Radial Pulse Field (GPU)",
};

// Formation IDs match the switch in `engine-webgl/formationsGLSL.ts`.
const MATH_FORMATION_ID: Record<string, number> = {
  superform: 0,
  hopfFibration: 1,
  radialPulse: 2,
  sineSurge: 3,
  stellarator: 4,
  fibonacciSequence: 5,
  plasmaSheet: 6,
  noiseBloom: 7,
  harmonicShell: 8,
  strangeAttractor: 9,
  lorenzDrift: 10,
  organicBreath: 11,
  spiralVortex: 12,
  helicalTwist: 13,
  superformGPU: 0,
  hopfFibrationGPU: 1,
  radialPulseGPU: 2,
  fibonacciSequenceGPU: 5,
};

export function createAltScene(type: AltSceneType, w: number, h: number): AltSceneApi {
  switch (type) {
    case "meshGrid": return new MeshGridScene(w, h);
    case "wavyForm": return new WavyFormScene(w, h);
    case "dotGrid": return new DotGridScene(w, h);
    case "frequencyBars": return new FrequencyBarsScene(w, h);
    case "texturedSphere": return new TexturedSphereScene(w, h);
    case "galaxy": return new GalaxyScene(w, h);
    // Bare original keys match the known-good live warm-gold math shader.
    case "superform": return new LiveMathParticleScene(w, h, "superform");
    case "spiralVortex": return new LiveMathParticleScene(w, h, "spiralVortex");
    case "hopfFibration": return new LiveMathParticleScene(w, h, "hopfFibration");
    case "fibonacciSequence": return new LiveMathParticleScene(w, h, "fibonacciSequence");
    case "radialPulse": return new LiveMathParticleScene(w, h, "radialPulse");
    // Park fiber-strand Hopf (distinct renderer, not a MathParticleScene mode).
    case "hopfTubes": return new HopfFibrationPointsScene(w, h);
    // Explicit live shader aliases (kept for stable curated routing).
    case "mathSuperform": return new LiveMathParticleScene(w, h, "superform");
    case "mathFibonacci": return new LiveMathParticleScene(w, h, "fibonacciSequence");
    case "mathRadialPulse": return new LiveMathParticleScene(w, h, "radialPulse");
    case "mathCurlPlasma": return new LiveMathParticleScene(w, h, "curlPlasma");
    case "mathHopfCathedral": return new LiveMathParticleScene(w, h, "hopfCathedral");
    case "mathKnotChoir": return new LiveMathParticleScene(w, h, "knotChoir");
    case "mathLorenzEmber": return new LiveMathParticleScene(w, h, "lorenzEmber");
    case "mathPhyllotacticGalaxy": return new LiveMathParticleScene(w, h, "phyllotacticGalaxy");
    case "mathReactionSkin": return new LiveMathParticleScene(w, h, "reactionSkin");
    case "mathSphericalHarmonicShell": return new LiveMathParticleScene(w, h, "sphericalHarmonicShell");
    case "mathSongPlanetCore": return new LiveMathParticleScene(w, h, "songPlanetCore");
    // GPGPU 250k-particle variants used by curated (GPU) pads.
    case "superformGPU":
    case "hopfFibrationGPU":
    case "fibonacciSequenceGPU":
    case "radialPulseGPU":
      return new SuperformGPUScene(w, h, MATH_FORMATION_ID[type]);
    // Remaining math-formation orphans — still GPGPU-only on local.
    case "sineSurge":
    case "stellarator":
    case "plasmaSheet":
    case "noiseBloom":
    case "harmonicShell":
    case "strangeAttractor":
    case "lorenzDrift":
    case "organicBreath":
    case "helicalTwist":
      return new SuperformGPUScene(w, h, MATH_FORMATION_ID[type]);
  }
}
